import { MultiSelectTreeChangeEvent, getMultiSelectTreeValue, MultiSelectTree } from "@progress/kendo-react-dropdowns";
import { useLocalization } from "@progress/kendo-react-intl";
import React, { useEffect } from "react";
import { enMessages } from "../../../messages/en-US";
import { expandedState, processMultiSelectTreeData } from "../Pages/MultiselectTree-Data-Operations";

interface Props {
  data: any[] | undefined,
  disabled: boolean,
  onChange: (selectedData: any) => void,
  tag: string,
  initialData: any[],
  textField: string,
  popupWidth?: number | string,
  popupHeight?: number | string,
  tagCounts?: { selected: number; total: number },
  resetKey?: number,
}

const dataItemKey = "id";

/** Rows that represent "select all" in the flat list (English + localized root label). */
function isSelectAllListRow(
  item: { name?: string },
  localizedSelectAll: string
): boolean {
  if (!item?.name) {
    return false;
  }
  return item.name === 'Select All' || item.name === localizedSelectAll;
}

/** Root + every node under it (depth-first), shallow-cloned — same shape as clicking “Select All” on the full tree. */
function fullSelectionFromReportTree(tree: any[], subItemsField: string): any[] {
  const root = tree[0];
  if (!root) return [];
  const out: any[] = [{ ...root }];
  const walk = (items: any[]) => {
    for (const item of items ?? []) {
      out.push({ ...item });
      const children = item[subItemsField];
      if (children?.length) walk(children);
    }
  };
  walk(root[subItemsField] ?? []);
  return out;
}

export default function ReportFilter(props: Props) {

  const localizationService = useLocalization();
  const localizedSelectAll = localizationService.toLanguageString('custom.selectAll', enMessages.custom.selectAll);

  const [reportList, setrReportList] = React.useState<any[]>([]);
  const [selectedItems, setSelectedItems] = React.useState<any[]>([]);
  const [filterList, setFilterList] = React.useState<{ value: string; field?: string } | null>(null);
  const [filterInputValue, setFilterInputValue] = React.useState('');
  const [expanded, setExpanded] = React.useState<any[]>([]);
  /** While the tree filter input is used, we replace the value with matching leaves only; restore this when the user clears the input (X) or closes the popup. */
  const selectionBeforeFilterRef = React.useRef<any[] | null>(null);
  const propsOnChangeRef = React.useRef(props.onChange);
  propsOnChangeRef.current = props.onChange;

  /**
   * Rebuild the tree from `initialData` (keep items list from root; fallback uses `data`).
   * Selection is synced in a separate effect so clearing the filter input does not race with `initialData` churn.
   */
  useEffect(() => {
    if (!props.initialData?.length || !props.initialData[0]) {
      return;
    }

    const root = props.initialData[0];
    let items = Array.isArray(root.items) ? root.items.map((item: any) => ({ ...item })) : [];

    // Fallback for callers (e.g. ComplianceReport) where initialData is recreated empty on every
    // render and the actual item list lives in props.data instead.
    if (items.length === 0 && Array.isArray(props.data) && props.data.length > 0) {
      items = props.data
        .filter((d: any) => d[dataItemKey] !== root[dataItemKey])
        .map((item: any) => ({ ...item }));
    }

    const clonedRoot = { ...root, items };
    setrReportList([clonedRoot]);
    setExpanded((prev) => (prev.length === 0 ? [clonedRoot[dataItemKey]] : prev));
  }, [props.initialData, props.data]);

  useEffect(() => {
    setSelectedItems(Array.isArray(props.data) ? props.data.slice() : []);
  }, [props.data]);

  const checkField = "checkField";
  const checkIndeterminateField = "checkIndeterminateField";
  const subItemsField = "items";
  const expandField = "expanded";
  const textField = props.textField;
  const fields = {
    dataItemKey,
    checkField,
    checkIndeterminateField,
    expandField,
    subItemsField,
  };

  useEffect(() => {
    if (props.resetKey !== undefined) {
      setFilterList(null);
      setFilterInputValue('');
      selectionBeforeFilterRef.current = null;
    }
  }, [props.resetKey]);

  const filterIsActive = Boolean(filterList?.value);

  const treeData = React.useMemo(
    () =>
      processMultiSelectTreeData(reportList, {
        expanded: expanded,
        value: selectedItems,
        filter: filterList,
        ...fields,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expanded, selectedItems, filterList, reportList]
  );

  /** All leaf nodes visible in the current filtered tree (excludes root). */
  const filteredLeaves = React.useMemo(() => {
    if (!filterIsActive || !treeData?.[0]) return [];
    const getLeaves = (items: any[]): any[] => {
      if (!items?.length) return [];
      const leaves: any[] = [];
      for (const item of items) {
        const children = item[subItemsField];
        if (!children?.length) {
          const raw = { ...item };
          delete raw[checkField];
          delete raw[checkIndeterminateField];
          delete raw[expandField];
          leaves.push(raw);
        } else {
          leaves.push(...getLeaves(children));
        }
      }
      return leaves;
    };
    return getLeaves(treeData[0][subItemsField] ?? []);
  }, [treeData, filterIsActive]);

  /**
   * When filter is active, rename the root "Select All" node to "Select all X results"
   * so the option is visible inside the popup — just like Excel's filter behaviour.
   */
  const treeDataForDisplay = React.useMemo(() => {
    if (!filterIsActive || !treeData?.[0] || filteredLeaves.length === 0) return treeData;
    const label = `Select all ${filteredLeaves.length} result${filteredLeaves.length !== 1 ? 's' : ''}`;
    return [{ ...treeData[0], name: label }];
  }, [treeData, filterIsActive, filteredLeaves.length]);

  const rootId = reportList[0]?.[dataItemKey];

  const onChange = (event: MultiSelectTreeChangeEvent) => {
    // Main chip-strip Clear button: Kendo sends operation "clear" and getMultiSelectTreeValue returns [].
    // Product UX: treat as "Select All", not empty selection.
    if ((event as MultiSelectTreeChangeEvent & { operation?: string }).operation === 'clear') {
      const selectAll = fullSelectionFromReportTree(reportList, subItemsField);
      setSelectedItems(selectAll);
      props.onChange(selectAll);
      return;
    }

    const allSelected = getMultiSelectTreeValue(reportList, { ...fields, ...event, value: selectedItems });

    // When filter is active and user checks the root ("Select all X results"), select only filtered leaves.
    const rootWasSelected = selectedItems.some((i) => i[dataItemKey] === rootId);
    const rootIsNowSelected = allSelected.some((i) => i[dataItemKey] === rootId);

    if (filterIsActive && !rootWasSelected && rootIsNowSelected) {
      const existingIds = new Set(selectedItems.map((i) => i[dataItemKey]));
      const toAdd = filteredLeaves.filter((i) => !existingIds.has(i[dataItemKey]));
      const merged = [...selectedItems, ...toAdd];
      setSelectedItems(merged);
      props.onChange(merged);
      return;
    }

    setSelectedItems(allSelected);
    props.onChange(allSelected);
  };

  const onExpandChange = React.useCallback(
    (event) => setExpanded(expandedState(event.item, dataItemKey, expanded)),
    [expanded]
  );

  const getAllLeaves = (items: any[]): any[] => {
    const leaves: any[] = [];
    for (const item of items) {
      const children = item[subItemsField];
      if (!children?.length) {
        leaves.push(item);
      } else {
        leaves.push(...getAllLeaves(children));
      }
    }
    return leaves;
  };

  const getLeavesMatchingFilter = (items: any[], q: string): any[] => {
    const leaves: any[] = [];
    for (const item of items) {
      const children = item[subItemsField];
      const text = String(item[textField] ?? '').toLowerCase();
      if (!children?.length) {
        if (text.includes(q)) leaves.push(item);
      } else {
        if (text.includes(q)) {
          leaves.push(...getAllLeaves(children));
        } else {
          leaves.push(...getLeavesMatchingFilter(children, q));
        }
      }
    }
    return leaves;
  };

  const onFilterChange = (event) => {
    const newFilter = event.filter;
    const rawValue = newFilter?.value ?? '';
    const trimmed = String(rawValue).trim();
    setFilterInputValue(rawValue);

    // Cleared filter box (X, backspace, or whitespace-only) — select everything in the current tree (expected UX).
    if (!trimmed) {
      setFilterList(null);
      selectionBeforeFilterRef.current = null;
      const selectAll = fullSelectionFromReportTree(reportList, subItemsField);
      setSelectedItems(selectAll);
      propsOnChangeRef.current(selectAll);
      return;
    }

    setFilterList(newFilter);

    if (!selectionBeforeFilterRef.current) {
      selectionBeforeFilterRef.current = Array.isArray(props.data)
        ? props.data.slice()
        : selectedItems.slice();
    }

    const q = trimmed.toLowerCase();
    const rootChildren = reportList[0]?.[subItemsField] ?? [];
    const matched = getLeavesMatchingFilter(rootChildren, q);

    if (matched.length > 0) {
      setSelectedItems(matched);
      props.onChange(matched);
    }

    const parentIds: any[] = [];
    const collectParentIds = (items: any[]) => {
      items?.forEach((item) => {
        if (item.items?.length) {
          parentIds.push(item[dataItemKey]);
          collectParentIds(item.items);
        }
      });
    };
    collectParentIds(reportList);
    setExpanded(parentIds);
  };

  const totalOptions = props.tagCounts?.total ?? Math.max(0, (props.data?.length || 1) - 1);
  const selectedCount = props.tagCounts?.selected ?? selectedItems.filter(
    (item) => !isSelectAllListRow(item, localizedSelectAll)
  ).length;
  const tagData = selectedItems.filter((item) => !isSelectAllListRow(item, localizedSelectAll));

  return (
    <>
      <div className="report-filter-multiselect w-full mr-2.5">
        <MultiSelectTree
          disabled={props.disabled}
          data={treeDataForDisplay}
          value={selectedItems}
          onChange={onChange}
          textField={textField}
          dataItemKey={dataItemKey}
          checkField={checkField}
          checkIndeterminateField={checkIndeterminateField}
          expandField={expandField}
          subItemsField={subItemsField}
          onExpandChange={onExpandChange}
          filterable={true}
          filter={filterInputValue}
          onFilterChange={onFilterChange}
          onClose={() => {
            setFilterList(null);
            setFilterInputValue('');
            // Keep the current auto-selected / user-selected state when closing the popup.
            selectionBeforeFilterRef.current = null;
          }}
          style={{
            maxHeight: '60px',
            overflowY: 'auto',
            overflowX: 'auto',
          }}
          popupSettings={{
            width: props.popupWidth ?? 'auto',
            height: props.popupHeight,
            popupClass: 'report-filter-multiselect-popup',
          }}
          tags={
            [{ text: `${selectedCount}/${totalOptions} ${props.tag}`, data: tagData }]
          }
        />
      </div>
    </>
  );
}
